import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { clientAPI, recentCallsAPI } from '../../../services/api';
import {
  Clock, Phone, PhoneIncoming, PhoneOutgoing, Search, Play, Pause,
  ChevronDown, ChevronUp, Download, Filter, Smile, Meh, Frown,
  FileText, Volume2, Loader2, AlertCircle, X, Calendar, User,
  Sparkles, CheckCircle, ListChecks, Target, MessageSquare, RefreshCw,
  Copy, ArrowRight, BarChart3, Activity, Timer
} from 'lucide-react';

const SentimentBadge = ({ sentiment }) => {
  const configs = {
    positive: { icon: Smile, color: 'text-green-400', bg: 'bg-green-400/10', label: 'Positive' },
    neutral: { icon: Meh, color: 'text-gray-400', bg: 'bg-gray-400/10', label: 'Neutral' },
    negative: { icon: Frown, color: 'text-red-400', bg: 'bg-red-400/10', label: 'Negative' }
  };
  
  const config = configs[sentiment?.sentiment] || configs.neutral;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </div>
  );
};

// Call Details Modal Component - Matches Reference Image
const CallDetailsModal = ({ call, onClose, agentName }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [mode, setMode] = useState('simple'); // simple or advanced
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00:00.00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toFixed(2).padStart(5, '0')}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', ' at');
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', ' at');
  };

  const toggleAudio = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const conversation = call.conversation || call.transcription || [];
  const hasRecording = call.recording_url || call.recording_local_path;

  const tabs = [
    { id: 'chat', label: 'Chat' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'post-actions', label: 'Post Actions' },
    { id: 'latency', label: 'Latency Profile' },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0f] border border-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-xl font-semibold text-white">
            Call Details (ID: {call.id?.slice(-7) || 'Unknown'})
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>Mode:</span>
              <div className="flex bg-[#1a1a1f] rounded-lg p-1">
                <button
                  onClick={() => setMode('simple')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    mode === 'simple' ? 'bg-teal-500 text-black' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Simple
                </button>
                <button
                  onClick={() => setMode('advanced')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    mode === 'advanced' ? 'bg-teal-500 text-black' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Advanced
                </button>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Audio Player */}
        {hasRecording && (
          <div className="px-6 py-4 border-b border-gray-800">
            <div className="bg-[#1a1a1f] rounded-lg p-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleAudio}
                  className="w-10 h-10 flex items-center justify-center text-white hover:text-teal-400 transition-colors"
                >
                  {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                </button>
                
                <div className="flex-1">
                  <div className="relative h-1 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-teal-500 rounded-full"
                      style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                    />
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={(e) => {
                        if (audioRef.current) {
                          audioRef.current.currentTime = parseFloat(e.target.value);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                
                <span className="text-gray-400 text-sm font-mono">
                  {formatTime(currentTime)} / {duration ? formatTime(duration) : 'Infinity:NaN'}
                </span>
                
                <a 
                  href={call.recording_url}
                  download
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <Download className="w-5 h-5 text-gray-400" />
                </a>
              </div>
              
              <audio
                ref={audioRef}
                src={call.recording_url}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setPlaying(false)}
              />
            </div>
          </div>
        )}

        {/* Call Info */}
        <div className="px-6 py-4 border-b border-gray-800 space-y-4">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-gray-500 text-sm mb-1">Source</p>
              <p className="text-white font-medium">{agentName || 'Outbound Call'}</p>
              <p className="text-gray-400 text-sm">Agent</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Call Time</p>
              <p className="text-white font-medium">{formatDateTime(call.started_at)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm mb-1">Call Info</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2 py-1 bg-[#1a1a1f] border border-gray-700 text-gray-300 text-xs rounded">
                  {call.type === 'test' ? 'Web Call' : call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                </span>
                <span className={`px-2 py-1 text-xs rounded ${
                  call.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                  call.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {call.status}
                </span>
                <span className="text-gray-400 text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(call.duration_seconds)}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <p className="text-gray-500 text-sm mb-1">Ended By</p>
            <p className="text-white">{call.ended_by || 'User'}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-800">
          <div className="flex gap-1 bg-[#1a1a1f] rounded-lg p-1 w-fit">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#0a0a0f] text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'chat' && (
            <div className="space-y-4">
              {conversation.length > 0 ? (
                conversation.map((msg, i) => {
                  const isAssistant = msg.speaker === 'agent' || msg.speaker === 'ai' || msg.speaker === 'assistant';
                  return (
                    <div key={i} className="flex gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAssistant ? 'bg-teal-500/20' : 'bg-cyan-500/20'
                      }`}>
                        <span className={`text-sm font-bold ${isAssistant ? 'text-teal-400' : 'text-cyan-400'}`}>
                          {isAssistant ? 'A' : 'U'}
                        </span>
                      </div>
                      <div className={`flex-1 rounded-lg p-4 ${
                        isAssistant ? 'bg-teal-500/10 border border-teal-500/30' : 'bg-[#1a1a1f] border border-gray-700'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`font-semibold ${isAssistant ? 'text-teal-400' : 'text-white'}`}>
                            {isAssistant ? 'Assistant' : 'Caller'}
                          </span>
                          <span className="text-gray-500 text-sm">
                            {msg.timestamp || formatMessageTime(call.started_at)}
                          </span>
                        </div>
                        <p className="text-gray-300 leading-relaxed">
                          {msg.text || msg.original_text || msg.content}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No conversation transcript available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analysis' && (
            <div className="space-y-6">
              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-teal-400" />
                  Call Analysis
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-500 text-sm">Sentiment</p>
                    <SentimentBadge sentiment={call.sentiment} />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Duration</p>
                    <p className="text-white">{formatDuration(call.duration_seconds)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Status</p>
                    <p className="text-white capitalize">{call.status}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Direction</p>
                    <p className="text-white capitalize">{call.direction}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'post-actions' && (
            <div className="space-y-4">
              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4">Post-Call Actions</h3>
                <p className="text-gray-500">No post-call actions configured for this call.</p>
              </div>
            </div>
          )}

          {activeTab === 'latency' && (
            <div className="space-y-4">
              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-400" />
                  Latency Profile
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-400">Average Response Time</span>
                    <span className="text-white font-mono">{call.latency?.avg || '~500'}ms</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-400">Speech-to-Text</span>
                    <span className="text-white font-mono">{call.latency?.stt || '~200'}ms</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-400">LLM Processing</span>
                    <span className="text-white font-mono">{call.latency?.llm || '~250'}ms</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-400">Text-to-Speech</span>
                    <span className="text-white font-mono">{call.latency?.tts || '~150'}ms</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Call Row Component - Matches Reference Image
const CallRow = ({ call, agentName, onClick }) => {
  const [copied, setCopied] = useState(false);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', ' at');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const copyId = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(call.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div 
      onClick={onClick}
      className="bg-[#0d1117] border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Phone Icon */}
          <div className="w-10 h-10 flex items-center justify-center">
            <Phone className="w-6 h-6 text-gray-500" />
          </div>
          
          {/* Direction Badge + Flow */}
          <div className="flex items-center gap-3">
            <span className={`px-2 py-1 text-xs font-medium rounded ${
              call.direction === 'outbound' 
                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
                : 'bg-green-500/20 text-green-400 border border-green-500/30'
            }`}>
              {call.direction === 'outbound' ? '↑ Outgoing' : '↓ Incoming'}
            </span>
            
            <span className="text-gray-400">Web Call</span>
            <ArrowRight className="w-4 h-4 text-gray-600" />
            <span className="text-white font-medium">Assistant</span>
          </div>
          
          {/* Date and Duration */}
          <div className="text-gray-500 text-sm">
            {formatDateTime(call.started_at)} • {formatDuration(call.duration_seconds)}
          </div>
          
          {/* Test Badge */}
          {call.type === 'test' && (
            <span className="px-2 py-1 text-xs font-medium rounded bg-teal-500/20 text-teal-400 border border-teal-500/30">
              Test Web Call
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          {/* ID with Copy */}
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <span>ID: #{call.id?.slice(-7) || 'Unknown'}</span>
            <button 
              onClick={copyId}
              className="p-1 hover:bg-gray-800 rounded transition-colors"
              title="Copy ID"
            >
              <Copy className={`w-4 h-4 ${copied ? 'text-teal-400' : 'text-gray-500'}`} />
            </button>
          </div>
          
          {/* Status Badge */}
          <span className={`px-3 py-1 text-xs font-medium rounded ${
            call.status === 'completed' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : call.status === 'failed'
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
            {call.status}
          </span>
        </div>
      </div>
    </div>
  );
};

const RecentCallsTab = ({ agent }) => {
  const { agentId } = useParams();
  const { clientUser } = useAuth();
  
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  
  // Filters
  const [directionFilter, setDirectionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');

  const fetchCalls = useCallback(async (isRefresh = false) => {
    if (!agentId || !clientUser?.id) return;
    
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await recentCallsAPI.getAgentCalls(clientUser.id, agentId, {});
      
      if (response.data.success) {
        let filteredCalls = response.data.calls;
        
        // Apply direction filter
        if (directionFilter !== 'all') {
          filteredCalls = filteredCalls.filter(call => call.direction === directionFilter);
        }
        
        // Apply status filter
        if (statusFilter !== 'all') {
          filteredCalls = filteredCalls.filter(call => call.status === statusFilter);
        }
        
        // Apply duration filter
        if (durationFilter !== 'all') {
          filteredCalls = filteredCalls.filter(call => {
            const duration = call.duration_seconds || 0;
            switch(durationFilter) {
              case 'short': return duration < 60;
              case 'medium': return duration >= 60 && duration < 300;
              case 'long': return duration >= 300;
              default: return true;
            }
          });
        }
        
        setCalls(filteredCalls);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [agentId, clientUser?.id, directionFilter, statusFilter, durationFilter]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const handleRefresh = () => {
    fetchCalls(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recent-calls-tab">
      {/* Header with Filters */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Recent Calls</h2>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <span>Filters</span>
            <span className="w-4 h-4 rounded-full border border-gray-600 inline-flex items-center justify-center text-xs">i</span>
          </div>
          
          {/* Direction Filter */}
          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm outline-none focus:border-teal-400"
          >
            <option value="all">All directions</option>
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
          </select>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm outline-none focus:border-teal-400"
          >
            <option value="all">All statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="in-progress">In Progress</option>
          </select>
          
          {/* Duration Filter */}
          <select
            value={durationFilter}
            onChange={(e) => setDurationFilter(e.target.value)}
            className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm outline-none focus:border-teal-400"
          >
            <option value="all">All durations</option>
            <option value="short">&lt; 1 min</option>
            <option value="medium">1-5 min</option>
            <option value="long">&gt; 5 min</option>
          </select>
          
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Calls List */}
      {calls.length > 0 ? (
        <div className="space-y-3">
          {calls.map((call) => (
            <CallRow 
              key={call.id} 
              call={call}
              agentName={agent?.name}
              onClick={() => setSelectedCall(call)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-12 text-center">
          <Clock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-400 mb-2">No calls yet</h3>
          <p className="text-gray-500 text-sm">
            Call history will appear here after your first call
          </p>
        </div>
      )}

      {/* Call Details Modal */}
      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          agentName={agent?.name}
          onClose={() => setSelectedCall(null)}
        />
      )}
    </div>
  );
};

export default RecentCallsTab;
